import { useMemo, useState } from 'react';
import {
  CommandBar,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  useTheme,
  type ICommandBarItemProps,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@auth';
import { buildDepartmentMenus } from '@/config/appCatalogUtils';

/**
 * Navigation bar component with logo, menu items, and user profile
 */
export function Navigation() {
  const navigate = useNavigate();
  const { user, logout, refreshToken } = useAuth();
  const theme = useTheme();
  const [showRolesDialog, setShowRolesDialog] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  const sortedRoles = [...(user?.roles ?? [])].sort((a, b) => a.localeCompare(b));

  const buildRolesClipboardText = (): string => {
    const displayName = user?.name || user?.username || 'Unknown User';
    const email = user?.email || user?.username || 'Unknown Email';
    const roleLines = sortedRoles.length > 0
      ? sortedRoles.map(role => `- ${role}`).join('\r\n')
      : '- No roles assigned';

    return [
      'Nettech Forms - User Roles',
      `User: ${displayName}`,
      `Email: ${email}`,
      `Role Count: ${sortedRoles.length}`,
      '',
      'Roles:',
      roleLines,
    ].join('\r\n');
  };

  const copyRolesToClipboard = async (): Promise<void> => {
    const formattedRoles = buildRolesClipboardText();

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(formattedRoles);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = formattedRoles;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopyStatus('Roles copied to clipboard.');
    } catch {
      setCopyStatus('Unable to copy roles. Please copy manually from this dialog.');
    }
  };

  const logo = (
    <div
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '4px',
        padding: '4px 12px',
        margin: '4px 8px',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0
      }}
    >
      <img
        src="https://cdn.net-cloud.com/api/files/resize?path=Logos%2Fnt_logo_horiz.png&height=60"
        alt="Networking Technologies"
        style={{ height: '52px', cursor: 'pointer', display: 'block' }}
        onClick={() => navigate('/')}
      />
    </div>
  );

  const menuItems: ICommandBarItemProps[] = useMemo(() => [
    {
      key: 'home',
      text: 'Home',
      iconProps: { iconName: 'Home' },
      onClick: () => navigate('/'),
    },
    ...buildDepartmentMenus(user, navigate),
  ], [user, navigate]);

  const farItems: ICommandBarItemProps[] = [
    {
      key: 'user',
      text: user?.name || user?.username || '',
      iconProps: { iconName: 'Contact' },
      subMenuProps: {
        items: [
          {
            key: 'profile',
            text: user?.email || '',
            iconProps: { iconName: 'Mail' },
            disabled: true,
          },
          {
            key: 'divider1',
            text: '-',
          },
          {
            key: 'refresh',
            text: 'Refresh Permissions',
            iconProps: { iconName: 'Refresh' },
            onClick: () => { void refreshToken(); },
            title: 'Refresh your roles and permissions without logging out',
          },
          {
            key: 'viewRoles',
            text: 'View Roles',
            iconProps: { iconName: 'BulletedList' },
            onClick: () => {
              setCopyStatus(null);
              setShowRolesDialog(true);
            },
            title: 'View and copy your current roles',
          },
          {
            key: 'divider2',
            text: '-',
          },
          {
            key: 'logout',
            text: 'Sign Out',
            iconProps: { iconName: 'SignOut' },
            onClick: () => { void logout(); },
          },
        ],
      },
    },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '76px' }}>
      {logo}
      <div style={{ flex: 1, minWidth: 0 }}>
        <CommandBar
          items={menuItems}
          farItems={farItems}
          styles={{
            root: {
              padding: 0,
              height: '76px'
            },
            primarySet: {
              minWidth: 0,
              flexGrow: 1,
              overflow: 'hidden'
            },
            secondarySet: {
              flexShrink: 0,
              marginLeft: 'auto'
            }
          }}
        />
      </div>

      <Dialog
        hidden={!showRolesDialog}
        onDismiss={() => setShowRolesDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Your Roles',
          subText: 'These are the roles currently in your authentication token.',
        }}
        modalProps={{
          isBlocking: false,
          styles: { main: { maxWidth: 640, width: '90vw' } },
        }}
      >
        <div style={{ marginBottom: 12, color: theme.palette.neutralPrimary }}>
          <strong>User:</strong> {user?.name || user?.username || 'Unknown User'}
        </div>
        <div style={{ marginBottom: 12, color: theme.palette.neutralPrimary }}>
          <strong>Role Count:</strong> {sortedRoles.length}
        </div>
        <div
          style={{
            maxHeight: 320,
            overflowY: 'auto',
            border: `1px solid ${theme.palette.neutralLight}`,
            borderRadius: 4,
            padding: 12,
            backgroundColor: theme.palette.neutralLighterAlt,
            color: theme.palette.neutralPrimary,
          }}
        >
          {sortedRoles.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {sortedRoles.map(role => (
                <li key={role} style={{ marginBottom: 4 }}>{role}</li>
              ))}
            </ul>
          ) : (
            <div>No roles assigned.</div>
          )}
        </div>
        {copyStatus && (
          <div
            style={{
              marginTop: 10,
              color: copyStatus.startsWith('Unable') ? theme.palette.redDark : theme.palette.greenDark,
            }}
          >
            {copyStatus}
          </div>
        )}
        <DialogFooter>
          <PrimaryButton
            text="Copy to Clipboard"
            iconProps={{ iconName: 'Copy' }}
            onClick={() => { void copyRolesToClipboard(); }}
          />
          <DefaultButton text="Close" onClick={() => setShowRolesDialog(false)} />
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default Navigation;
